"use client";

import { Fragment } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BarChart3,
  Check,
  ClipboardCheck,
  GraduationCap,
  KeyRound,
  LifeBuoy,
  Settings2,
  ShieldCheck,
  Users,
} from "lucide-react";
import StructuredData from "@/components/common/StructuredData";
import ServiceFaq from "@/components/services/ServiceFaq";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import { CTAButton } from "@/components/ui/cta-button";
import { MDECBadge } from "@/components/ui/mdec-badge";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import GovernanceCallout from "@/components/ai-governance/GovernanceCallout";

function renderAccent(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="emphasis-underline">
        {part}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

const managedServiceFaqs: Array<{ question: string; answer: string }> = [
  {
    question: "What does the Claude Managed Service include?",
    answer:
      "We set up your Claude workspace, user access, governance rules, approved use cases, starter templates, and internal champions. After rollout, we provide ongoing administration, adoption support, usage reporting, cost reviews, and help when workflows or policies change.",
  },
  {
    question: "Is Claude onboarding the same as Claude training?",
    answer:
      "No. Onboarding is part of the managed service: workspace setup, access, policies, approved workflows, team rollout, and day-to-day support. Claude Training is a separate structured learning programme that develops practical AI skills through workshops, exercises, and assessments.",
  },
  {
    question: "Can you manage a Claude workspace we already use?",
    answer:
      "Yes. We begin with a short workspace and adoption review, document the current setup, identify access or governance gaps, and agree on an operating plan before making changes.",
  },
  {
    question: "How long does Claude onboarding take?",
    answer:
      "A focused rollout can usually begin within two to four weeks. Timing depends on the number of users, identity and access requirements, approval processes, and how many business workflows are included in the first phase.",
  },
  {
    question: "Who owns our Claude workspace and business data?",
    answer:
      "Your organisation retains ownership and administrative control. We configure the controls available to your selected Claude plan, document the setup, and operate only within the access and responsibilities agreed with your team.",
  },
];

const managedStages: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
  points: string[];
}> = [
  {
    title: "Set up",
    description:
      "Prepare Claude for company use with clear ownership, access, and governance.",
    icon: Settings2,
    points: [
      "Workspace and administrator setup",
      "User access and role planning",
      "Usage policies and approval rules",
    ],
  },
  {
    title: "Onboard",
    description:
      "Introduce Claude through real business work, not a generic product tour.",
    icon: Users,
    points: [
      "Priority use-case selection",
      "Starter templates and approved workflows",
      "Team champions and phased rollout",
    ],
  },
  {
    title: "Run",
    description:
      "Keep the service organised, supported, and aligned with company policy.",
    icon: LifeBuoy,
    points: [
      "Ongoing administration and support",
      "Usage and cost reporting",
      "Workflow and policy updates",
    ],
  },
  {
    title: "Improve",
    description:
      "Use adoption evidence to strengthen the workflows that create real value.",
    icon: BarChart3,
    points: [
      "Adoption and usage reviews",
      "Prompt and workflow improvements",
      "Quarterly improvement priorities",
    ],
  },
];

const managedOutcomes: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Clear access and accountability",
    description:
      "Everyone knows who owns the workspace, who can use it, and which decisions still require human approval.",
    icon: KeyRound,
  },
  {
    title: "Useful workflows from day one",
    description:
      "Teams receive approved starting points for the work they actually do instead of being left with an empty chat window.",
    icon: ClipboardCheck,
  },
  {
    title: "Governance that stays current",
    description:
      "Policies, access, and approved use cases are reviewed as the organisation and Claude capabilities change.",
    icon: ShieldCheck,
  },
];

interface ClaudeManagedServiceProps {
  locale?: Locale;
}

export default function ClaudeManagedService({
  locale = defaultLocale,
}: ClaudeManagedServiceProps) {
  const prefersReducedMotion = useReducedMotion();

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: {
      duration: 0.55,
      delay,
      ease: [0.21, 0.6, 0.35, 1] as const,
    },
  });

  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: {
      duration: 0.5,
      ease: [0.21, 0.6, 0.35, 1] as const,
    },
  };

  return (
    <div className="bg-background">
      <StructuredData type="faq" faqs={managedServiceFaqs} />

      <section className="bg-background pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <motion.p
                {...reveal(0)}
                className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
              >
                {t(locale, "Claude Managed Service · Malaysia")}
              </motion.p>
              <motion.h1
                {...reveal(0.08)}
                className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl"
              >
                {renderAccent(
                  t(locale, "Onboard Claude. Keep it [[working for your team]]."),
                )}
              </motion.h1>
              <motion.p
                {...reveal(0.16)}
                className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl"
              >
                {t(
                  locale,
                  "We set up Claude for your organisation, onboard employees through approved business workflows, and manage access, governance, adoption, support, and reporting after rollout.",
                )}
                {/* In-text anchor to the "Claude partner Malaysia" owner page (2026-10-08). */}
                {" "}
                {t(locale, "We run it as a ")}
                <Link
                  href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {t(locale, "Claude partner in Malaysia")}
                </Link>
                {t(locale, ", with the team in Selangor.")}
              </motion.p>
              <motion.div
                {...reveal(0.24)}
                className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
              >
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="w-full rounded-full sm:w-auto"
                >
                  {t(locale, "Plan your Claude rollout")}
                </CTAButton>
                <Link
                  href="#what-we-manage"
                  className="group inline-flex w-full items-center justify-center gap-2 text-base font-semibold text-neutral-emphasis transition hover:text-primary sm:w-auto"
                >
                  {t(locale, "See what we manage")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </motion.div>
            </div>

            <motion.div
              {...reveal(0.2)}
              className="overflow-hidden rounded-3xl bg-hero-background"
            >
              <Image
                src="/images/services/claude-managed-service-editorial.jpg"
                alt={t(
                  locale,
                  "Editorial illustration of a governed Claude rollout across a business team",
                )}
                width={768}
                height={512}
                priority
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
        <div className="mt-16 h-px w-full bg-border" aria-hidden />
      </section>

      <section className="bg-background py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...reveal(0.3)}
            className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 lg:justify-between"
          >
            <MDECBadge variant="hero" />
            <AnthropicPartnerBadge variant="hero" />
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-emphasis">
              <Users className="h-4 w-4 text-primary" aria-hidden />
              {t(locale, "Team onboarding and adoption")}
            </span>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-emphasis">
              <LifeBuoy className="h-4 w-4 text-primary" aria-hidden />
              {t(locale, "Local managed support")}
            </span>
          </motion.div>
        </div>
        <div className="mt-10 h-px w-full bg-border" aria-hidden />
      </section>

      <section
        id="what-we-manage"
        className="scroll-mt-24 bg-background py-24 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "What we manage")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(
                t(locale, "From first setup to [[everyday adoption]]"),
              )}
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
              {t(
                locale,
                "The service begins with onboarding and continues as your organisation, policies, employees, and use cases change.",
              )}
            </p>
          </motion.div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {managedStages.map(
              ({ title, description, icon: Icon, points }, index) => (
                <motion.article
                  key={title}
                  {...inView}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.06,
                    ease: [0.21, 0.6, 0.35, 1] as const,
                  }}
                  className="flex h-full flex-col rounded-2xl border border-border bg-card p-7"
                >
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-neutral-emphasis">
                    {t(locale, title)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, description)}
                  </p>
                  <ul className="mt-5 space-y-3">
                    {points.map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-2.5 text-sm text-neutral-muted"
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
                          aria-hidden
                        />
                        {t(locale, point)}
                      </li>
                    ))}
                  </ul>
                </motion.article>
              ),
            )}
          </div>
        </div>
      </section>

      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Business outcomes")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(t(locale, "Claude becomes a service your team can [[rely on]]"))}
            </h2>
          </motion.div>
          <div className="grid gap-5 md:grid-cols-3">
            {managedOutcomes.map(({ title, description, icon: Icon }) => (
              <motion.article
                key={title}
                {...inView}
                className="rounded-2xl border border-border bg-card p-7"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Onboarding or training?")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(t(locale, "Two needs, [[two clear services]]"))}
            </h2>
          </motion.div>
          <div className="grid gap-5 md:grid-cols-2">
            <motion.article
              {...inView}
              className="rounded-2xl border border-primary/30 bg-card p-8"
            >
              <Settings2 className="h-7 w-7 text-primary" aria-hidden />
              <h3 className="mt-5 font-display text-2xl font-semibold text-neutral-emphasis">
                {t(locale, "Claude Managed Service")}
              </h3>
              <p className="mt-3 leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "For organisations asking: how do we introduce Claude and operate it properly every day? It covers setup, access, governance, onboarding, administration, adoption support, and reporting.",
                )}
              </p>
            </motion.article>
            <motion.article
              {...inView}
              className="rounded-2xl border border-border bg-card p-8"
            >
              <GraduationCap className="h-7 w-7 text-primary" aria-hidden />
              <h3 className="mt-5 font-display text-2xl font-semibold text-neutral-emphasis">
                {t(locale, "Claude & AI Training")}
              </h3>
              <p className="mt-3 leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "For organisations asking: how do our employees develop practical and responsible AI skills? It covers structured courses, workshops, exercises, and assessments.",
                )}
              </p>
              <Link
                href={getLocalizedPathname(
                  "/services/training-education/",
                  locale,
                )}
                className="mt-6 inline-flex items-center gap-2 font-semibold text-primary"
              >
                {t(locale, "Explore Claude training")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.article>
          </div>
          {/* A managed-Claude page owes the reader the model rationale. */}
          <div className="mt-12 text-center">
            <Link
              href={getLocalizedPathname(
                "/anthropic-claude-partner-malaysia-singapore/",
                locale,
              )}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition-all hover:gap-3"
            >
              {t(locale, "Why we build on Claude, not OpenAI")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-background px-4 py-12 sm:px-6 lg:px-8">
        <GovernanceCallout locale={locale} variant="rollout" placement="claude_managed_service" />
      </section>

      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "FAQ")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {renderAccent(t(locale, "Questions about [[onboarding and support]]"))}
            </h2>
          </motion.div>
          <ServiceFaq items={managedServiceFaqs} />
        </div>
      </section>

      <section className="bg-background pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-3xl bg-hero-background p-10 sm:p-14"
          >
            <div className="max-w-3xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {t(locale, "Start a conversation")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {t(locale, "Ready to introduce Claude across your team?")}
              </h2>
              <p className="mt-4 text-lg text-primary-contrast/70">
                {t(
                  locale,
                  "We will review your users, priority workflows, access requirements, and governance needs, then propose a practical onboarding and managed-service plan.",
                )}
              </p>
              <div className="mt-8">
                <CTAButton
                  href="/contact/"
                  size="large"
                  className="rounded-full"
                >
                  {t(locale, "Plan your Claude rollout")}
                </CTAButton>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
